import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, rupee } from "../lib/api";
import { formatSaleDate } from "../lib/saleInvoice";
import { ArrowLeft, RotateCcw } from "lucide-react";
import { toast } from "sonner";

function returnableQty(line) {
  return Math.max(0, (line.quantity || 0) - (line.returned_quantity || 0));
}

export default function PurchaseDetail() {
  const { id } = useParams();
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [returnQty, setReturnQty] = useState({});
  const [returnNotes, setReturnNotes] = useState("");
  const [returning, setReturning] = useState(false);
  const [showReturn, setShowReturn] = useState(false);

  const load = () => {
    setLoading(true);
    return api.get(`/purchases/${id}`)
      .then((r) => {
        setPurchase(r.data);
        const init = {};
        r.data.items.forEach((it) => {
          const max = returnableQty(it);
          if (max > 0) init[it.product_id] = max;
        });
        setReturnQty(init);
      })
      .catch(() => toast.error("Purchase not found"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const canReturn = purchase?.items?.some((it) => returnableQty(it) > 0);

  const setQty = (productId, value, max) => {
    const n = Math.min(max, Math.max(0, parseInt(value, 10) || 0));
    setReturnQty((s) => ({ ...s, [productId]: n }));
  };

  const submitReturn = async () => {
    const items = Object.entries(returnQty)
      .filter(([, qty]) => qty > 0)
      .map(([product_id, quantity]) => ({ product_id, quantity }));
    if (items.length === 0) {
      toast.error("Set return quantity for at least one item");
      return;
    }

    setReturning(true);
    try {
      const { data } = await api.post(`/purchases/${id}/returns`, {
        items,
        notes: returnNotes || undefined,
      });
      toast.success(`Return ${data.return_no} accepted`);
      setShowReturn(false);
      setReturnNotes("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Return failed");
    } finally {
      setReturning(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center" style={{ color: "var(--text-muted)" }}>Loading…</div>;
  }

  if (!purchase) {
    return (
      <div className="p-8 text-center">
        <p className="mb-4">Purchase not found.</p>
        <Link to="/app/purchases" className="text-sm font-semibold" style={{ color: "var(--primary)" }}>← Back</Link>
      </div>
    );
  }

  const supplier = purchase.supplier_snapshot || {};

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="purchase-detail-root">
      <Link to="/app/purchases" className="inline-flex items-center gap-2 text-sm font-semibold mb-6" style={{ color: "var(--primary)" }}>
        <ArrowLeft size={16} /> Purchases
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl font-mono">{purchase.purchase_no}</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            {formatSaleDate(purchase.created_at)} · {supplier.name || "Supplier"}
            {supplier.phone ? ` · ${supplier.phone}` : ""}
          </p>
        </div>
        {canReturn && (
          <button
            type="button"
            onClick={() => setShowReturn((v) => !v)}
            className="h-11 px-5 rounded-md border font-semibold inline-flex items-center gap-2 tactile-btn bg-white"
            style={{ borderColor: "var(--primary)", color: "var(--primary)" }}
          >
            <RotateCcw size={16} /> {showReturn ? "Cancel return" : "Return to supplier"}
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="card-soft p-4">
          <div className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Purchase total</div>
          <div className="text-xl font-bold num mt-1">{rupee(purchase.total)}</div>
        </div>
        <div className="card-soft p-4">
          <div className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Returned</div>
          <div className="text-xl font-bold num mt-1" style={{ color: purchase.returned_total > 0 ? "var(--error)" : "inherit" }}>
            {rupee(purchase.returned_total || 0)}
          </div>
        </div>
        <div className="card-soft p-4">
          <div className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Status</div>
          <div className="text-sm font-semibold mt-2 capitalize">
            {purchase.status === "full_return" ? "Fully returned" : purchase.status === "partial_return" ? "Partial return" : "Active"}
          </div>
        </div>
      </div>

      {purchase.notes && (
        <div className="card-soft p-4 mb-6 text-sm" style={{ color: "var(--text-muted)" }}>
          <span className="font-medium text-[var(--text-main)]">Notes:</span> {purchase.notes}
        </div>
      )}

      <div className="card-soft overflow-hidden mb-6">
        <div className="px-4 py-3 border-b font-semibold text-sm" style={{ borderColor: "var(--border-soft)" }}>
          Purchase items
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>
                <th className="px-4 py-2">Product</th>
                <th className="px-4 py-2 text-right">Qty</th>
                <th className="px-4 py-2 text-right">Returned</th>
                <th className="px-4 py-2 text-right">Unit cost</th>
                <th className="px-4 py-2 text-right">Line total</th>
              </tr>
            </thead>
            <tbody>
              {purchase.items.map((it) => (
                <tr key={it.product_id} className="border-t" style={{ borderColor: "var(--border-soft)" }}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{it.product_name}</div>
                    <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{it.barcode}</div>
                  </td>
                  <td className="px-4 py-3 text-right num">{it.quantity}</td>
                  <td className="px-4 py-3 text-right num" style={{ color: it.returned_quantity > 0 ? "var(--error)" : "var(--text-muted)" }}>
                    {it.returned_quantity || 0}
                  </td>
                  <td className="px-4 py-3 text-right num">{rupee(it.unit_cost)}</td>
                  <td className="px-4 py-3 text-right num font-medium">{rupee(it.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showReturn && canReturn && (
        <div className="card-soft p-5 mb-6 border-2" style={{ borderColor: "var(--primary)" }}>
          <h2 className="font-display font-bold text-lg mb-4">Return to supplier</h2>
          <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
            Adjust qty per line (defaults to what’s still returnable). Stock is reduced when you accept the return.
          </p>
          <div className="space-y-3 mb-4">
            {purchase.items.filter((it) => returnableQty(it) > 0).map((it) => {
              const max = returnableQty(it);
              return (
                <div key={it.product_id} className="flex flex-wrap items-center gap-3 justify-between border rounded-md p-3" style={{ borderColor: "var(--border-soft)" }}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{it.product_name}</div>
                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>Max returnable: {max}</div>
                  </div>
                  <input
                    type="number"
                    min={0}
                    max={max}
                    value={returnQty[it.product_id] ?? 0}
                    onChange={(e) => setQty(it.product_id, e.target.value, max)}
                    className="w-24 h-10 px-2 rounded-md border text-center num"
                    style={{ borderColor: "var(--border-soft)" }}
                  />
                </div>
              );
            })}
          </div>
          <div className="mb-4">
            <label className="text-sm font-medium">Return reason / notes</label>
            <input
              value={returnNotes}
              onChange={(e) => setReturnNotes(e.target.value)}
              className="mt-1.5 w-full h-11 px-3 rounded-md border bg-white"
              style={{ borderColor: "var(--border-soft)" }}
              placeholder="Damaged, wrong size, excess stock…"
            />
          </div>
          <button
            type="button"
            disabled={returning}
            onClick={submitReturn}
            className="h-11 px-8 rounded-md text-white font-semibold tactile-btn"
            style={{ background: "var(--primary)" }}
          >
            {returning ? "Processing…" : "Accept return"}
          </button>
        </div>
      )}

      {purchase.returns?.length > 0 && (
        <div className="card-soft overflow-hidden">
          <div className="px-4 py-3 border-b font-semibold text-sm" style={{ borderColor: "var(--border-soft)" }}>
            Return history ({purchase.returns.length})
          </div>
          <div className="divide-y" style={{ borderColor: "var(--border-soft)" }}>
            {purchase.returns.map((ret) => (
              <div key={ret.id} className="px-4 py-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-mono font-semibold text-sm" style={{ color: "var(--primary)" }}>{ret.return_no}</span>
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full uppercase font-semibold" style={{ background: ret.return_type === "full" ? "#fee2e2" : "#fef3c7", color: ret.return_type === "full" ? "#b91c1c" : "#92400e" }}>
                      {ret.return_type}
                    </span>
                  </div>
                  <div className="text-sm num font-semibold" style={{ color: "var(--error)" }}>{rupee(ret.total)}</div>
                </div>
                <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                  {formatSaleDate(ret.created_at)}
                  {ret.notes ? ` · ${ret.notes}` : ""}
                </div>
                <ul className="mt-2 text-sm space-y-0.5">
                  {ret.items.map((it) => (
                    <li key={`${ret.id}-${it.product_id}`} className="flex justify-between gap-2">
                      <span className="truncate">{it.product_name} × {it.quantity}</span>
                      <span className="num shrink-0">{rupee(it.line_total)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
